import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  Checkbox,
  Paper,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Star as StarIcon } from '@mui/icons-material';
import UniversalScoreBadge from './UniversalScoreBadge';
import FreshnessBadge from '../CreditCardComparison/FreshnessBadge';
import { computeFreshness, toFreshnessBadgeStatus } from './freshness';
import { trackClickApply } from './analytics';
import { getLogoPath } from './logoMap';

/**
 * Ranked product row - mobile + desktop layouts.
 * Visual Declutter:
 *   - Unified 3-chip cap with priority ordering
 *   - Bonus chip as outline red (not solid)
 *   - #1 card uses blue (not orange)
 *   - All config chips neutral gray
 *   - Mobile description truncated
 */
const ProductRow = ({
  item,
  rank,
  config,
  category,
  selectedForCompare,
  onToggleCompare,
  onViewDetail,
  onExplainerClick,
  recommendationLine,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSelected = selectedForCompare.some((c) => c.id === item.id);
  const compareDisabled = selectedForCompare.length >= 4 && !isSelected;
  const isFirst = rank === 1;

  const configChips = (config.chips?.(item) || []).filter((c) => c.show !== false);
  const institution = item[config.institutionField] || '';
  const name = item[config.nameField] || '';

  // Logo - try institution first, fall back to product name, then logoField (for translated names)
  const logoName = config.logoField ? (item[config.logoField] || '') : '';
  const logoPath = getLogoPath(institution) || getLogoPath(name) || getLogoPath(logoName);

  // Freshness
  const freshnessInfo = computeFreshness(item.data_ops?.last_verified_at);
  const badgeStatus = item.data_ops ? toFreshnessBadgeStatus(freshnessInfo.status) : null;

  const bonusValue = item.welcome_bonus?.active ? item.welcome_bonus.value_cad : null;
  const description = item.content?.short_description_es;
  const applyUrl = config.getApplyUrl?.(item);

  // "Ideal para ti" — only meaningful for top 3, otherwise every row has it
  const isIdealParaTi = rank <= 3 && (
    item.ranking?.personas?.includes('NEWCOMER')
    || item.newcomer_program?.eligible
    || item.target_audience === 'ALL_NEWCOMERS'
  );

  // ── Unified chip system: priority-ordered, all neutral styling ──
  const allChips = [];

  // Priority 1: "Ideal para ti" (top 3 only)
  if (isIdealParaTi) {
    allChips.push({ type: 'ideal' });
  }

  // Priority 2: Bonus (as chip, not loud color)
  if (bonusValue > 0) {
    allChips.push({ type: 'bonus', value: bonusValue });
  }

  // Priority 3: FreshnessBadge (only #1)
  if (isFirst && badgeStatus) {
    allChips.push({ type: 'freshness', status: badgeStatus });
  }

  // Priority 4+: Config chips
  configChips.forEach((c) => {
    allChips.push({ type: 'config', label: c.label });
  });

  const visibleChips = allChips.slice(0, isFirst ? 3 : 2);

  const handleApplyClick = () => {
    if (applyUrl && category && item.id) {
      trackClickApply(category, String(item.id), applyUrl);
      // Fire dedicated referral event for affiliate partners
      if (applyUrl.includes('utm_medium=referral') && window.plausible) {
        const bankName = (institution || name || '').toLowerCase().replace(/\s.*$/, '');
        window.plausible('Referral Click', { props: { bank: bankName } });
      }
    }
  };

  // ── Neutral chip style shared across all types ──
  const neutralChipSx = {
    fontWeight: 500,
    fontSize: '0.7rem',
    height: 22,
    bgcolor: 'neutral.100',
    color: 'text.secondary',
    border: 'none',
  };

  // ── Render a single chip by type ──
  const renderChip = (chip, i) => {
    switch (chip.type) {
      case 'ideal':
        return (
          <Chip
            key="ideal"
            label="Ideal para ti"
            size="small"
            sx={{ ...neutralChipSx, fontWeight: 600, color: 'primary.main' }}
          />
        );
      case 'bonus':
        return (
          <Chip
            key="bonus"
            label={`$${chip.value.toLocaleString('en-CA')} bono`}
            size="small"
            sx={{ ...neutralChipSx, fontWeight: 600 }}
          />
        );
      case 'freshness':
        return <FreshnessBadge key="freshness" status={chip.status} />;
      case 'config':
        return (
          <Chip
            key={`cfg-${i}`}
            label={chip.label}
            size="small"
            sx={neutralChipSx}
          />
        );
      default:
        return null;
    }
  };

  // ── Chip strip ──
  const chipStrip = visibleChips.length > 0 && (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {visibleChips.map(renderChip)}
    </Box>
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: isFirst ? { xs: 2.5, md: 3.5 } : { xs: 2, md: 3 },
        mb: isFirst ? 3 : 2,
        borderRadius: 3,
        border: isFirst ? '2px solid' : '1px solid',
        borderColor: isFirst ? 'primary.main' : 'divider',
        bgcolor: isFirst ? 'neutral.25' : 'background.paper',
        boxShadow: isFirst
          ? '0 4px 20px rgba(0,0,0,0.08)'
          : '0 1px 4px rgba(0,0,0,0.04)',
        transition: 'box-shadow 0.2s ease',
        position: 'relative',
        animation: `fadeInRow 0.25s ease-out ${(rank - 1) * 0.04}s both`,
        '@keyframes fadeInRow': {
          from: { opacity: 0, transform: 'translateY(6px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
        '&:hover': {
          boxShadow: isFirst
            ? '0 6px 28px rgba(0,0,0,0.10)'
            : '0 2px 12px rgba(0,0,0,0.06)',
        },
      }}
    >
      {/* "Mejor opción" badge for #1 */}
      {isFirst && (
        <Chip
          icon={<StarIcon sx={{ fontSize: '0.8rem' }} />}
          label="Mejor opción"
          size="small"
          sx={{
            position: 'absolute',
            top: -12,
            left: 16,
            fontWeight: 700,
            fontSize: '0.75rem',
            height: 24,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            '& .MuiChip-icon': { color: 'primary.contrastText' },
          }}
        />
      )}

      {isMobile ? (
        /* ── MOBILE ── */
        <Box sx={{ pt: isFirst ? 1 : 0 }}>
          {/* Top: rank + logo + name */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
            <RankBadge rank={rank} />
            <InstitutionLogo src={logoPath} alt={institution} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                {institution}
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, lineHeight: 1.3, fontSize: '1rem' }}
              >
                {name}
              </Typography>
              <Box sx={{ mt: 0.75 }}>
                <UniversalScoreBadge item={item} onExplainerClick={null} />
              </Box>
            </Box>
          </Box>

          {/* Recommendation line for #1 */}
          {isFirst && recommendationLine && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: 'primary.main',
                fontWeight: 600,
                mb: 1,
                pl: 0.5,
              }}
            >
              {recommendationLine}
            </Typography>
          )}

          {/* Description - truncated */}
          {description && (
            <Typography
              variant="body2"
              noWrap
              sx={{
                color: 'text.secondary',
                fontSize: '0.875rem',
                mb: 1,
                lineHeight: 1.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {description}
            </Typography>
          )}

          {/* Unified chip strip */}
          {chipStrip && <Box sx={{ mb: 1.5 }}>{chipStrip}</Box>}

          {/* Actions - CTA hierarchy */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {applyUrl && (
                <Button
                  variant="contained"
                  href={applyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleApplyClick}
                  sx={{
                    bgcolor: 'secondary.main',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    minHeight: 44,
                    '&:hover': { bgcolor: 'secondary.dark' },
                  }}
                >
                  Aplicar ahora
                </Button>
              )}
              <Button
                variant={applyUrl ? 'outlined' : 'contained'}
                onClick={() => onViewDetail(item)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: '8px',
                  fontSize: '0.875rem',
                  minHeight: 44,
                  ...(applyUrl
                    ? { borderColor: 'divider', color: 'text.primary' }
                    : { bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }),
                }}
              >
                Ver detalles
              </Button>
            </Box>
            {config.layoutMode === 'ranked' && (
              <Box
                onClick={() => !compareDisabled && onToggleCompare(item)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  px: 0.5,
                  py: 0.5,
                  borderRadius: '6px',
                  cursor: compareDisabled ? 'default' : 'pointer',
                  bgcolor: isSelected ? 'rgba(31,58,95,0.06)' : 'transparent',
                  minHeight: 44,
                  minWidth: 44,
                  justifyContent: 'center',
                }}
              >
                <Checkbox
                  checked={isSelected}
                  disabled={compareDisabled}
                  onChange={() => onToggleCompare(item)}
                  size="small"
                  sx={{
                    color: 'primary.main',
                    p: 1,
                    '&.Mui-checked': { color: 'primary.main' },
                  }}
                  inputProps={{ 'aria-label': `Comparar ${name}` }}
                />
              </Box>
            )}
          </Box>
        </Box>
      ) : (
        /* ── DESKTOP ── */
        <Box sx={{ pt: isFirst ? 0.5 : 0 }}>
          {/* Row 1 (primary): rank + logo + name/institution + score + CTA buttons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <RankBadge rank={rank} />
            <InstitutionLogo src={logoPath} alt={institution} />

            {/* Info */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                {institution}
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1.3 }}
              >
                {name}
              </Typography>
            </Box>

            {/* Score */}
            <Box sx={{ flexShrink: 0 }}>
              <UniversalScoreBadge item={item} onExplainerClick={null} />
            </Box>

            {/* Actions - CTA hierarchy */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
              {applyUrl && (
                <Button
                  variant="contained"
                  size="small"
                  href={applyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleApplyClick}
                  sx={{
                    bgcolor: isFirst ? 'secondary.main' : 'primary.main',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '8px',
                    whiteSpace: 'nowrap',
                    fontSize: '0.875rem',
                    '&:hover': { bgcolor: isFirst ? 'secondary.dark' : 'primary.dark' },
                  }}
                >
                  Aplicar ahora
                </Button>
              )}
              <Button
                variant="outlined"
                size="small"
                onClick={() => onViewDetail(item)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: '8px',
                  whiteSpace: 'nowrap',
                  borderColor: 'divider',
                  color: 'text.primary',
                  fontSize: '0.875rem',
                }}
              >
                Ver detalles
              </Button>
            </Box>
          </Box>

          {/* Row 2 (secondary): description line + chips line */}
          <Box sx={{ ml: 14, mt: 0.75 }}>
            {/* Sub-row A: description + compare checkbox */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {description && (
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.disabled',
                    fontSize: '0.8125rem',
                    lineHeight: 1.5,
                    flex: 1,
                    minWidth: 0,
                  }}
                  noWrap
                >
                  {description}
                </Typography>
              )}

              {/* Compare checkbox pushed to far right */}
              {config.layoutMode === 'ranked' && (
                <Box
                  onClick={() => !compareDisabled && onToggleCompare(item)}
                  sx={{
                    ml: 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    py: 0.25,
                    borderRadius: '8px',
                    flexShrink: 0,
                    cursor: compareDisabled ? 'default' : 'pointer',
                    bgcolor: isSelected ? 'rgba(31,58,95,0.06)' : 'transparent',
                    transition: 'background-color 0.15s ease',
                    '&:hover': compareDisabled ? {} : { bgcolor: 'rgba(31,58,95,0.04)' },
                  }}
                >
                  <Checkbox
                    checked={isSelected}
                    disabled={compareDisabled}
                    onChange={() => onToggleCompare(item)}
                    size="small"
                    sx={{
                      color: 'primary.main',
                      p: 0.5,
                      '&.Mui-checked': { color: 'primary.main' },
                    }}
                    inputProps={{ 'aria-label': `Comparar ${name}` }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: isSelected ? 'primary.main' : 'text.disabled',
                      fontWeight: isSelected ? 600 : 400,
                      fontSize: '0.75rem',
                      userSelect: 'none',
                    }}
                  >
                    Comparar
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Sub-row B: chips on their own line */}
            {visibleChips.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.75 }}>
                {visibleChips.map(renderChip)}
              </Box>
            )}
          </Box>

          {/* Recommendation line for #1 (desktop) */}
          {isFirst && recommendationLine && (
            <Box
              sx={{
                fontSize: '0.8125rem',
                fontWeight: 500,
                color: 'primary.main',
                bgcolor: 'rgba(31,58,95,0.04)',
                borderRadius: '8px',
                px: 2,
                py: 1,
                mt: 1.5,
                ml: 14,
              }}
            >
              {recommendationLine}
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
};

/* ── Institution logo (wider container for bank wordmarks) ── */
const InstitutionLogo = ({ src, alt }) => {
  if (!src) return null;
  return (
    <Box
      sx={{
        width: { xs: 52, md: 72 },
        height: 40,
        borderRadius: '8px',
        bgcolor: 'neutral.50',
        border: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        overflow: 'hidden',
        p: 0.5,
      }}
    >
      <Box
        component="img"
        src={src}
        alt={alt}
        width={64}
        height={32}
        sx={{
          maxWidth: '100%',
          maxHeight: '100%',
          objectFit: 'contain',
        }}
      />
    </Box>
  );
};

/* ── Rank badge (inline) ── */
const RankBadge = ({ rank }) => (
  <Box
    sx={{
      width: 32,
      height: 32,
      borderRadius: '50%',
      bgcolor: rank <= 3 ? 'warning.main' : 'neutral.200',
      color: rank <= 3 ? 'warning.contrastText' : 'text.secondary',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 700,
      fontSize: '0.875rem',
      flexShrink: 0,
    }}
  >
    {rank}
  </Box>
);

export default ProductRow;
